<template>
  <div class="cf">
    <div class="cf-card">
      <div class="cf-head">
        <div class="cf-head-text">
          <h3 class="cf-title">Cost attribution flow</h3>
          <p class="cf-sub">How leavers, stayers and joiners moved cost per person-year between two reports.</p>
        </div>
        <div class="cf-controls">
          <select v-if="availableContracts.length > 1" v-model="selectedContractId" class="cf-select" aria-label="Contract" @change="onContractChange">
            <option v-for="c in availableContracts" :key="c.contract_id" :value="c.contract_id">
              {{ c.contract_name && c.contract_name !== c.contract_id ? `${demoLabel(c.contract_name, 'contract')} (${demoLabel(c.contract_id, 'contract')})` : demoLabel(c.contract_id, 'contract') }}
            </option>
          </select>
          <select v-model="groupBy" class="cf-select" aria-label="Group by" @change="selectedTin = null; patients = []; fetchData()">
            <option value="tin">By practice</option>
            <option value="facility">By facility</option>
            <option value="staff">By provider</option>
          </select>
          <template v-if="reportPairs.length > 1">
            <span class="cf-dates">
              <select v-model="selectedDateA" class="cf-select" aria-label="From report" @change="onDateAChange">
                <option v-for="d in dateAOptions" :key="d" :value="d">{{ d }}</option>
              </select>
              <svg class="cf-dates-arrow" viewBox="0 0 24 12" width="20" height="10" aria-hidden="true"><path d="M0 6h21M16 1l5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              <select v-model="selectedDateB" class="cf-select" aria-label="To report" @change="onDateBChange">
                <option v-for="d in dateBOptions" :key="d" :value="d">{{ d }}</option>
              </select>
            </span>
          </template>
          <label class="cf-toggle">
            <input v-model="riskAdjusted" type="checkbox" />
            <span>Risk-adjusted</span>
          </label>
        </div>
      </div>

      <LoadingIndicator v-if="loading" message="Loading cost flow..." />
      <p v-else-if="!summary" class="cf-empty">No cost attribution data for this selection.</p>

      <template v-else>
        <div class="cf-bridge" role="group" aria-label="Cost per person-year bridge from the earlier to the later report">
          <div class="cf-tile cf-tile-anchor">
            <div class="cf-tile-l">{{ selectedDateA || 'Earlier' }}</div>
            <div class="cf-tile-n">{{ fmtDollar(panelA.costPerPY) }}<small>/PY</small></div>
            <div class="cf-tile-s">{{ fmt(panelA.patients) }} patients · {{ fmtPY(panelA.py) }} PY<template v-if="riskAdjusted"> · risk {{ panelA.risk?.toFixed(2) || '—' }}</template></div>
          </div>
          <span class="cf-op" aria-hidden="true">−</span>
          <div class="cf-tile">
            <div class="cf-tile-l">Leavers removed</div>
            <div class="cf-tile-n" :class="tone(leaverEffect)">{{ fmtSigned(leaverEffect) }}<small>/PY</small></div>
            <div class="cf-tile-s">Leavers were {{ fmtDollar(summary.Leavers?.costPerPY) }}/PY</div>
          </div>
          <span class="cf-op" aria-hidden="true">+</span>
          <div class="cf-tile">
            <div class="cf-tile-l">Stayer drift</div>
            <div class="cf-tile-n" :class="tone(stayerDrift)">{{ fmtSigned(stayerDrift) }}<small>/PY</small></div>
            <div class="cf-tile-s">{{ fmtDollar(summary.Stayers?.priorCostPerPY) }} → {{ fmtDollar(summary.Stayers?.costPerPY) }}</div>
          </div>
          <span class="cf-op" aria-hidden="true">+</span>
          <div class="cf-tile">
            <div class="cf-tile-l">Joiners added</div>
            <div class="cf-tile-n" :class="tone(joinerEffect)">{{ fmtSigned(joinerEffect) }}<small>/PY</small></div>
            <div class="cf-tile-s">Joiners are {{ fmtDollar(summary.Joiners?.costPerPY) }}/PY</div>
          </div>
          <span class="cf-op" aria-hidden="true">=</span>
          <div class="cf-tile cf-tile-anchor">
            <div class="cf-tile-l">{{ selectedDateB || 'Later' }}</div>
            <div class="cf-tile-n">{{ fmtDollar(panelB.costPerPY) }}<small>/PY</small></div>
            <div class="cf-tile-s">{{ fmt(panelB.patients) }} patients · {{ fmtPY(panelB.py) }} PY<template v-if="riskAdjusted"> · risk {{ panelB.risk?.toFixed(2) || '—' }}</template></div>
          </div>
        </div>
        <p class="cf-bridge-net">
          Net <b :class="tone(costDelta)">{{ fmtSigned(costDelta) }}/PY</b> <span>({{ fmtPct(costDeltaPct) }})</span>
        </p>

        <div class="cf-segments">
          <div v-for="cat in ['Leavers', 'Stayers', 'Joiners']" :key="cat" class="cf-seg" :class="`is-${cat.toLowerCase()}`">
            <div class="cf-seg-head">
              <span class="cf-seg-name">{{ cat }}</span>
              <span class="cf-seg-n">{{ fmt(summary[cat]?.patients || 0) }}<small> patients</small></span>
            </div>
            <template v-if="cat === 'Stayers'">
              <dl class="cf-seg-grid">
                <div><dt>Before</dt><dd>{{ fmtDollar(summary.Stayers?.priorCostPerPY) }}<small>/PY</small></dd></div>
                <div><dt>After</dt><dd>{{ fmtDollar(summary.Stayers?.costPerPY) }}<small>/PY</small></dd></div>
                <div><dt>Change</dt><dd :class="tone(stayerDrift)">{{ fmtSigned(stayerDrift) }}<small>{{ fmtPct(stayerDriftPct) }}</small></dd></div>
              </dl>
              <div v-if="riskAdjusted" class="cf-seg-s">Risk {{ (summary.Stayers?.priorRisk || 0).toFixed(2) }} → {{ (summary.Stayers?.risk || 0).toFixed(2) }}</div>
            </template>
            <template v-else>
              <div class="cf-seg-s">{{ fmtDollar(summary[cat]?.costPerPY) }}/PY · risk {{ (summary[cat]?.risk || 0).toFixed(2) }}</div>
              <div v-if="summary[cat]?.deceased" class="cf-seg-s">{{ fmt(summary[cat].deceased) }} deceased ({{ fmtPct(summary[cat].deceased / summary[cat].patients) }})</div>
            </template>
          </div>
        </div>
      </template>
    </div>

    <div v-if="!loading && summary" class="cf-card cf-card-table">
      <div class="cf-head cf-head-tight">
        <h3 class="cf-title">{{ groupByLabel }}</h3>
        <span class="cf-sub">Click a row for its patients</span>
      </div>
      <div class="cf-scroll">
        <table class="cf-table">
          <thead>
            <tr>
              <th rowspan="2" class="is-left" @click="sortBy('name')">{{ groupByColumnLabel }}</th>
              <th colspan="2" class="is-group is-leavers">Leavers</th>
              <th colspan="4" class="is-group">Stayers</th>
              <th colspan="2" class="is-group is-joiners">Joiners</th>
              <th rowspan="2" @click="sortBy('netSpend')">Δ benchmark</th>
            </tr>
            <tr>
              <th @click="sortBy('leftPts')">Pts</th>
              <th @click="sortBy('leftCost')">$/PY</th>
              <th @click="sortBy('stayPts')">Pts</th>
              <th @click="sortBy('stayBeforeCost')">Before</th>
              <th @click="sortBy('stayAfterCost')">After</th>
              <th @click="sortBy('stayDelta')">Δ $/PY</th>
              <th @click="sortBy('joinPts')">Pts</th>
              <th @click="sortBy('joinCost')">$/PY</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in sortedPractices" :key="row.tin" :class="{ 'is-selected': selectedTin === row.tin }" @click="selectPractice(row)">
              <td class="is-left sensitive" :data-spoof-kind="groupBy === 'facility' ? 'facility' : groupBy === 'staff' ? 'provider' : 'practice'">{{ row.name }}</td>
              <td class="is-leavers">{{ row.leftPts || '—' }}</td>
              <td>{{ row.leftCost ? fmtDollar(row.leftCost) : '—' }}</td>
              <td>{{ row.stayPts || '—' }}</td>
              <td>{{ row.stayBeforeCost ? fmtDollar(row.stayBeforeCost) : '—' }}</td>
              <td>{{ row.stayAfterCost ? fmtDollar(row.stayAfterCost) : '—' }}</td>
              <td :class="row.stayDelta == null ? '' : tone(row.stayDelta)"><b>{{ row.stayDelta != null ? fmtSigned(row.stayDelta) : '—' }}</b></td>
              <td class="is-joiners">{{ row.joinPts || '—' }}</td>
              <td>{{ row.joinCost ? fmtDollar(row.joinCost) : '—' }}</td>
              <td :class="tone(row.netSpend)"><b>{{ fmtDollarM(row.netSpend) }}</b></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="selectedTin" class="cf-patients">
        <LoadingIndicator v-if="loadingPatients" message="Loading patients..." />
        <template v-else-if="patients.length">
          <div class="cf-head cf-head-tight">
            <h4 class="cf-title">
              <span class="sensitive" :data-spoof-kind="groupBy === 'facility' ? 'facility' : groupBy === 'staff' ? 'provider' : 'practice'">{{ practiceNames[selectedTin] || selectedTin }}</span>
              <span class="cf-sub">{{ patients.length }} patients<template v-if="selectedCategory">, {{ selectedCategory.toLowerCase() }}</template></span>
            </h4>
            <div class="cf-controls">
              <select v-model="selectedCategory" class="cf-select" aria-label="Category" @change="fetchPatients">
                <option :value="null">All categories</option>
                <option value="Leavers">Leavers</option>
                <option value="Stayers">Stayers</option>
                <option value="Joiners">Joiners</option>
              </select>
              <button type="button" class="cf-link" @click="selectedTin = null; patients = [];">Close</button>
            </div>
          </div>
          <div class="cf-scroll cf-scroll-sm">
            <table class="cf-table">
              <thead>
                <tr>
                  <th class="is-left">Patient</th>
                  <th>Category</th>
                  <th>Total cost</th>
                  <th>IP</th>
                  <th>SNF</th>
                  <th>OP</th>
                  <th>Part B</th>
                  <th>Hospice</th>
                  <th>PY</th>
                  <th>HCC</th>
                  <th>Deceased</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="pt in patients" :key="pt.BENE_MBI_ID" :class="`is-${(pt.CATEGORY || '').toLowerCase()}`">
                  <td class="is-left sensitive">{{ pt.BENE_LAST_NAME }}, {{ pt.BENE_FIRST_NAME }}</td>
                  <td>{{ pt.CATEGORY }}</td>
                  <td>{{ fmtDollar(pt.TOT_COST) }}</td>
                  <td>{{ fmtDollar(pt.IP_COST) }}</td>
                  <td>{{ fmtDollar(pt.SNF_COST) }}</td>
                  <td>{{ fmtDollar(pt.OP_COST) }}</td>
                  <td>{{ fmtDollar(pt.PB_COST) }}</td>
                  <td>{{ fmtDollar(pt.HOSPICE_COST) }}</td>
                  <td>{{ pt.PERSON_YEARS?.toFixed(2) || '—' }}</td>
                  <td>{{ pt.HCC_SCORE?.toFixed(2) || '—' }}</td>
                  <td>{{ pt.IS_DECEASED ? 'Yes' : '' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { demoLabel } from '@/utils/demoSpoof';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';

export default {
  name: 'CostAttributionFlow',
  components: { LoadingIndicator },
  props: {
    metricId: { type: Number, default: null },
    entityId: { type: Number, default: null },
    year: { type: Number, default: null },
    metricLinks: { type: Array, default: null },
    pageType: { type: String, default: null },
  },
  data() {
    return {
      loading: false,
      allResults: [],
      reportPairs: [],
      selectedDateA: null,
      selectedDateB: null,
      filteredResults: [],
      practiceNames: {},
      sortKey: 'name',
      sortAsc: true,
      riskAdjusted: false,
      groupBy: 'tin',
      selectedTin: null,
      selectedCategory: null,
      patients: [],
      loadingPatients: false,
      availableContracts: [],
      selectedContractId: null,
    };
  },
  computed: {
    dateAOptions() {
      // All A dates that have at least one B > A (i.e. any A that appears in a pair)
      const set = new Set(this.reportPairs.map(p => p.REPORT_DT_A));
      return [...set].sort();
    },
    dateBOptions() {
      // B dates must be strictly after the selected A
      if (!this.selectedDateA) {
        const set = new Set(this.reportPairs.map(p => p.REPORT_DT_B));
        return [...set].sort();
      }
      const set = new Set(
        this.reportPairs
          .filter(p => p.REPORT_DT_A === this.selectedDateA)
          .map(p => p.REPORT_DT_B)
      );
      return [...set].sort();
    },
    panelRows() {
      return this.filteredResults.filter(r => r.PRIMARY_TIN === '_PANEL');
    },
    tinRows() {
      return this.filteredResults.filter(r => r.PRIMARY_TIN !== '_PANEL' && r.PRIMARY_TIN != null);
    },
    summary() {
      if (!this.panelRows.length) return null;
      const cats = {};
      for (const row of this.panelRows) {
        const ra = this.riskAdjusted;
        cats[row.CATEGORY] = {
          patients: row.PATIENT_COUNT || 0,
          py: row.TOTAL_PERSON_YEARS || 0,
          spend: ra ? (row.RISK_ADJ_TOTAL_WEIGHTED_SPEND || 0) : (row.TOTAL_WEIGHTED_SPEND || 0),
          costPerPY: ra ? (row.RISK_ADJ_TOTAL_COST_PER_PY || null) : (row.WEIGHTED_TOTAL_COST_PER_PY || null),
          risk: row.AVG_RISK_SCORE || null,
          deceased: row.DECEASED_COUNT || 0,
          priorCostPerPY: ra ? (row.PRIOR_RISK_ADJ_TOTAL_COST_PER_PY || null) : (row.PRIOR_WEIGHTED_TOTAL_COST_PER_PY || null),
          priorPY: row.PRIOR_TOTAL_PERSON_YEARS || null,
          priorRisk: row.PRIOR_AVG_RISK_SCORE || null,
        };
      }
      return cats;
    },
    panelA() {
      if (!this.summary) return {};
      const l = this.summary.Leavers || {};
      const s = this.summary.Stayers || {};
      // Panel A = leavers (at report_a cost) + stayers (at report_a cost)
      const lPY = l.py || 0;
      const sPriorPY = s.priorPY || 0;
      const py = lPY + sPriorPY;
      const lSpend = l.spend || 0;
      const sSpend = (s.priorCostPerPY || 0) * sPriorPY;
      // Panel A risk: PY-weighted avg of leavers risk + stayers prior risk
      const lRiskPY = (l.risk || 0) * lPY;
      const sRiskPY = (s.priorRisk || 0) * sPriorPY;
      const risk = py > 0 ? (lRiskPY + sRiskPY) / py : null;
      return { patients: (l.patients || 0) + (s.patients || 0), py, costPerPY: py > 0 ? (lSpend + sSpend) / py : 0, risk };
    },
    panelB() {
      if (!this.summary) return {};
      const j = this.summary.Joiners || { patients: 0, py: 0, spend: 0 };
      const s = this.summary.Stayers || { patients: 0, py: 0, spend: 0 };
      const py = (j.py || 0) + (s.py || 0);
      const spend = (j.spend || 0) + (s.spend || 0);
      // Panel B risk: PY-weighted avg of joiners risk + stayers current risk
      const jRiskPY = (j.risk || 0) * (j.py || 0);
      const sRiskPY = (s.risk || 0) * (s.py || 0);
      const risk = py > 0 ? (jRiskPY + sRiskPY) / py : null;
      return { patients: (j.patients || 0) + (s.patients || 0), py, costPerPY: py > 0 ? spend / py : 0, risk };
    },
    costDelta() {
      return (this.panelB.costPerPY || 0) - (this.panelA.costPerPY || 0);
    },
    costDeltaPct() {
      return this.panelA.costPerPY ? this.costDelta / this.panelA.costPerPY : 0;
    },
    stayersPriorCost() {
      return this.summary?.Stayers?.priorCostPerPY || 0;
    },
    stayersCurrentCost() {
      return this.summary?.Stayers?.costPerPY || 0;
    },
    leaverEffect() {
      // Removing leavers: stayers-only at prior cost minus Panel A cost
      return this.stayersPriorCost - (this.panelA.costPerPY || 0);
    },
    stayerDrift() {
      return this.stayersCurrentCost - this.stayersPriorCost;
    },
    stayerDriftPct() {
      if (!this.stayersPriorCost) return 0;
      return (this.stayersCurrentCost - this.stayersPriorCost) / this.stayersPriorCost;
    },
    joinerEffect() {
      // Adding joiners: Panel B cost minus stayers-only at current cost
      return (this.panelB.costPerPY || 0) - this.stayersCurrentCost;
    },
    practices() {
      if (!this.tinRows.length) return [];
      const byTin = {};
      const ra = this.riskAdjusted;
      for (const row of this.tinRows) {
        const tin = row.PRIMARY_TIN || 'Unknown';
        if (!byTin[tin]) byTin[tin] = {
          tin, name: this.practiceNames[tin] || tin,
          // Leavers (report_a values)
          leftPts: 0, leftPY: 0, leftSpend: 0, leftRiskPY: 0, leftRiskDenom: 0,
          // Joiners (report_b values)
          joinPts: 0, joinPY: 0, joinSpend: 0, joinRiskPY: 0, joinRiskDenom: 0,
          // Stayers: prior = report_a, current = report_b
          stayPts: 0,
          stayPriorPY: 0, stayPriorSpend: 0, stayPriorRiskPY: 0, stayPriorRiskDenom: 0,
          stayCurrentPY: 0, stayCurrentSpend: 0, stayCurrentRiskPY: 0, stayCurrentRiskDenom: 0,
        };
        const b = byTin[tin];
        const spend = ra ? (row.RISK_ADJ_TOTAL_WEIGHTED_SPEND || 0) : (row.TOTAL_WEIGHTED_SPEND || 0);
        const py = row.TOTAL_PERSON_YEARS || 0;
        const risk = row.AVG_RISK_SCORE || 0;
        if (row.CATEGORY === 'Leavers') {
          b.leftPts += row.PATIENT_COUNT; b.leftPY += py; b.leftSpend += spend;
          if (risk) { b.leftRiskPY += risk * py; b.leftRiskDenom += py; }
        }
        if (row.CATEGORY === 'Joiners') {
          b.joinPts += row.PATIENT_COUNT; b.joinPY += py; b.joinSpend += spend;
          if (risk) { b.joinRiskPY += risk * py; b.joinRiskDenom += py; }
        }
        if (row.CATEGORY === 'Stayers') {
          b.stayPts += row.PATIENT_COUNT;
          b.stayCurrentPY += py; b.stayCurrentSpend += spend;
          if (risk) { b.stayCurrentRiskPY += risk * py; b.stayCurrentRiskDenom += py; }
          // Prior period
          const priorPY = row.PRIOR_TOTAL_PERSON_YEARS || 0;
          const priorSpend = ra ? ((row.PRIOR_RISK_ADJ_TOTAL_COST_PER_PY || 0) * priorPY) : ((row.PRIOR_WEIGHTED_TOTAL_COST_PER_PY || 0) * priorPY);
          const priorRisk = row.PRIOR_AVG_RISK_SCORE || 0;
          b.stayPriorPY += priorPY; b.stayPriorSpend += priorSpend;
          if (priorRisk) { b.stayPriorRiskPY += priorRisk * priorPY; b.stayPriorRiskDenom += priorPY; }
        }
      }
      const panelAvg = this.panelA.costPerPY || 0;
      return Object.values(byTin).map(b => {
        // Before = leavers + stayers at report_a
        const beforePY = b.leftPY + b.stayPriorPY;
        const beforeSpend = b.leftSpend + b.stayPriorSpend;
        const beforeCost = beforePY > 0 ? beforeSpend / beforePY : null;
        const beforeRiskDenom = b.leftRiskDenom + b.stayPriorRiskDenom;
        const beforeRisk = beforeRiskDenom > 0 ? (b.leftRiskPY + b.stayPriorRiskPY) / beforeRiskDenom : null;
        // After = joiners + stayers at report_b
        const afterPY = b.joinPY + b.stayCurrentPY;
        const afterSpend = b.joinSpend + b.stayCurrentSpend;
        const afterCost = afterPY > 0 ? afterSpend / afterPY : null;
        const afterRiskDenom = b.joinRiskDenom + b.stayCurrentRiskDenom;
        const afterRisk = afterRiskDenom > 0 ? (b.joinRiskPY + b.stayCurrentRiskPY) / afterRiskDenom : null;
        // Benchmark impact: what did this practice's churn do to the ACO per-capita
        const leftCost = b.leftPY > 0 ? b.leftSpend / b.leftPY : null;
        const joinCost = b.joinPY > 0 ? b.joinSpend / b.joinPY : null;
        const leaverImpact = leftCost != null ? (panelAvg - leftCost) * b.leftPY : 0;
        const joinerImpact = joinCost != null ? (joinCost - panelAvg) * b.joinPY : 0;
        const stayBeforeCost = b.stayPriorPY > 0 ? b.stayPriorSpend / b.stayPriorPY : null;
        const stayAfterCost = b.stayCurrentPY > 0 ? b.stayCurrentSpend / b.stayCurrentPY : null;
        const stayDelta = stayBeforeCost != null && stayAfterCost != null ? stayAfterCost - stayBeforeCost : null;
        const stayDeltaPct = stayBeforeCost ? (stayAfterCost - stayBeforeCost) / stayBeforeCost : null;
        return {
          ...b,
          leftCost, joinCost,
          stayCost: stayAfterCost,
          stayBeforeCost, stayAfterCost, stayDelta, stayDeltaPct,
          beforePts: b.leftPts + b.stayPts,
          beforeCost, beforeRisk,
          afterPts: b.joinPts + b.stayPts,
          afterCost, afterRisk,
          netSpend: leaverImpact + joinerImpact,
        };
      });
    },
    groupByLabel() {
      return { tin: 'By Practice (TIN)', facility: 'By Facility', staff: 'By Staff (NPI)' }[this.groupBy] || 'By Practice (TIN)';
    },
    groupByColumnLabel() {
      return { tin: 'Practice', facility: 'Facility', staff: 'Provider' }[this.groupBy] || 'Practice';
    },
    sortedPractices() {
      const key = this.sortKey;
      const dir = this.sortAsc ? 1 : -1;
      if (key === 'name') {
        return [...this.practices].sort((a, b) => (a.name || '').localeCompare(b.name || '') * dir);
      }
      return [...this.practices].sort((a, b) => ((a[key] || 0) - (b[key] || 0)) * dir);
    },
  },
  methods: {
    demoLabel,
    async fetchData() {
      this.loading = true;

      // On first load, discover which contracts have overlapping BEUR+ATTRIB data
      if (!this.availableContracts.length) {
        try {
          const discoResp = await axios.post('api/portfolio_management/cost-attribution-flow/contracts/');
          this.availableContracts = discoResp.data?.contracts || [];
        } catch { /* ignore */ }
      }

      // Pick contract: explicit selection > metricLinks > first available
      let contractId = this.selectedContractId;
      if (!contractId) {
        const fromLinks = this.metricLinks?.map(l => l.contract_id).filter(Boolean) || [];
        contractId = fromLinks.find(id => this.availableContracts.some(c => c.contract_id === id))
          || this.availableContracts[0]?.contract_id
          || null;
        this.selectedContractId = contractId;
      }

      if (!contractId) {
        this.loading = false;
        return;
      }

      const payload = { group_by: this.groupBy, contract_id: contractId };
      if (this.entityId) payload.entity_id = this.entityId;
      if (this.selectedDateA) payload.report_dt_a = this.selectedDateA;
      if (this.selectedDateB) payload.report_dt_b = this.selectedDateB;

      try {
        const resp = await axios.post('api/portfolio_management/cost-attribution-flow/', payload);
        this.allResults = resp.data.results || [];
        this.filteredResults = this.allResults;
        this.practiceNames = resp.data.tin_names || {};
        // Only refresh reportPairs if we don't have any yet — avoids churn on date changes
        if (!this.reportPairs.length) {
          this.reportPairs = (resp.data.report_pairs || []).map(p => ({
            ...p,
            key: `${p.REPORT_DT_A}|${p.REPORT_DT_B}`,
            label: `${p.REPORT_DT_A} → ${p.REPORT_DT_B}`,
          }));
          // Default to widest span (earliest A, latest B) on first load
          if (this.reportPairs.length && (!this.selectedDateA || !this.selectedDateB)) {
            const widest = this.reportPairs[this.reportPairs.length - 1];
            this.selectedDateA = widest.REPORT_DT_A;
            this.selectedDateB = widest.REPORT_DT_B;
            // Re-fetch now that dates are set
            await this.fetchData();
          }
        }
      } catch (err) {
        console.error('CostAttributionFlow error:', err);
      } finally {
        this.loading = false;
      }
    },
    filterData() {
      // Date picker changed — re-fetch from server for the new pair
      this.fetchData();
    },
    onDateAChange() {
      // If current B is not compatible with new A, pick the latest compatible B
      const validBs = this.reportPairs
        .filter(p => p.REPORT_DT_A === this.selectedDateA)
        .map(p => p.REPORT_DT_B);
      if (!validBs.includes(this.selectedDateB)) {
        this.selectedDateB = validBs[validBs.length - 1] || null;
      }
      this.selectedTin = null;
      this.patients = [];
      this.filterData();
    },
    onDateBChange() {
      // If current A is not compatible with new B, pick the earliest compatible A
      const validAs = this.reportPairs
        .filter(p => p.REPORT_DT_B === this.selectedDateB)
        .map(p => p.REPORT_DT_A);
      if (!validAs.includes(this.selectedDateA)) {
        this.selectedDateA = validAs[0] || null;
      }
      this.selectedTin = null;
      this.patients = [];
      this.filterData();
    },
    
    selectPractice(row) {
      if (this.selectedTin === row.tin) {
        this.selectedTin = null;
        this.patients = [];
        return;
      }
      this.selectedTin = row.tin;
      this.selectedCategory = null;
      this.patients = [];
      this.fetchPatients();
    },
    onContractChange() {
      // Reset transient state and reload for the new contract
      this.allResults = [];
      this.reportPairs = [];
      this.selectedDateA = null;
      this.selectedDateB = null;
      this.filteredResults = [];
      this.selectedTin = null;
      this.selectedCategory = null;
      this.patients = [];
      this.fetchData();
    },
    async fetchPatients() {
      if (!this.selectedTin || !this.selectedDateA || !this.selectedDateB) return;
      this.loadingPatients = true;
      const a = this.selectedDateA;
      const b = this.selectedDateB;
      const contractId = this.selectedContractId;
      if (!contractId) { this.loadingPatients = false; return; }
      try {
        const resp = await axios.post('api/portfolio_management/cost-attribution-flow/patients/', {
          contract_id: contractId,
          report_dt_a: a,
          report_dt_b: b,
          group_key: this.selectedTin,
          group_by: this.groupBy,
          category: this.selectedCategory || undefined,
        });
        this.patients = resp.data.patients || [];
      } catch (err) {
        console.error('fetchPatients error:', err);
        this.patients = [];
      } finally {
        this.loadingPatients = false;
      }
    },
    sortBy(key) {
      if (this.sortKey === key) this.sortAsc = !this.sortAsc;
      else { this.sortKey = key; this.sortAsc = key === 'name'; }
    },
    tone(n) {
      if (n == null || n === 0) return '';
      return n < 0 ? 'is-good' : 'is-bad';
    },
    fmtSigned(n) {
      if (n == null) return '—';
      return (n > 0 ? '+' : '') + this.fmtDollar(n);
    },
    fmt(n) { return n != null ? n.toLocaleString() : '—'; },
    fmtPY(n) { return n != null ? n.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'; },
    fmtDollar(n) {
      if (n == null) return '—';
      const abs = Math.abs(n);
      const prefix = n < 0 ? '-' : '';
      return prefix + '$' + abs.toLocaleString(undefined, { maximumFractionDigits: 0 });
    },
    fmtDollarM(n) {
      if (n == null) return '—';
      const m = n / 1e6;
      const prefix = m < 0 ? '-' : '+';
      return prefix + '$' + Math.abs(m).toFixed(1) + 'M';
    },
    fmtPct(n) {
      if (n == null) return '—';
      return (n >= 0 ? '+' : '') + (n * 100).toFixed(1) + '%';
    },
  },
  mounted() {
    this.fetchData();
  },
  watch: {
    entityId() { this.fetchData(); },
  },
};
</script>

<style scoped>
.cf { display: flex; flex-direction: column; gap: 16px; }
.cf-card { background: #fff; border: 1px solid var(--color-gray-200); border-radius: 10px; padding: 20px 22px; }
.cf-card-table { padding-bottom: 12px; }
.cf-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.cf-head-tight { align-items: baseline; margin-bottom: 10px; }
.cf-title { margin: 0; font-size: 14px; font-weight: 600; color: var(--color-gray-900); letter-spacing: -0.01em; }
.cf-title .cf-sub { margin-left: 8px; font-weight: 400; }
.cf-sub { margin: 2px 0 0; font-size: 12px; color: var(--color-gray-500); }
.cf-empty { margin: 8px 0 0; font-size: 13px; color: var(--color-gray-500); }
.cf-controls { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.cf-select { height: 32px; padding: 0 28px 0 10px; font: inherit; font-size: 12.5px; color: var(--color-gray-900); background: #fff; border: 1px solid var(--color-gray-300); border-radius: 8px; appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path d='M1 1l5 5 5-5' fill='none' stroke='%236b7280' stroke-width='1.6' stroke-linecap='round'/></svg>"); background-repeat: no-repeat; background-position: right 10px center; }
.cf-select:focus-visible { outline: 2px solid var(--color-accent); outline-offset: 1px; }
.cf-dates { display: inline-flex; align-items: center; gap: 6px; }
.cf-dates-arrow { color: var(--color-gray-400); }
.cf-toggle { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; color: var(--color-gray-700); cursor: pointer; user-select: none; }
.cf-toggle input { width: 14px; height: 14px; accent-color: var(--color-accent); }
.cf-link { font: inherit; font-size: 12.5px; color: var(--color-gray-600); background: none; border: 0; padding: 0 4px; cursor: pointer; }
.cf-link:hover { color: var(--color-gray-900); text-decoration: underline; }

.cf-bridge { display: grid; grid-template-columns: 1.15fr auto 1fr auto 1fr auto 1fr auto 1.15fr; align-items: stretch; gap: 10px; margin-top: 22px; }
.cf-tile { padding: 14px 16px; border: 1px solid var(--color-gray-200); border-radius: 10px; min-width: 0; }
.cf-tile-anchor { background: var(--color-gray-50); border-color: var(--color-gray-200); }
.cf-tile-l { font-size: 12px; color: var(--color-gray-500); }
.cf-tile-n { margin-top: 6px; font-size: 22px; font-weight: 600; line-height: 1; letter-spacing: -0.02em; color: var(--color-gray-900); font-variant-numeric: tabular-nums; white-space: nowrap; }
.cf-tile-n small { font-size: 12px; font-weight: 500; color: var(--color-gray-500); margin-left: 2px; letter-spacing: 0; }
.cf-tile-s { margin-top: 8px; font-size: 11.5px; color: var(--color-gray-500); line-height: 1.4; }
.cf-op { align-self: center; font-size: 20px; color: var(--color-gray-400); line-height: 1; }
.cf-bridge-net { margin: 12px 0 0; font-size: 13px; color: var(--color-gray-600); text-align: right; }
.cf-bridge-net b { font-weight: 600; font-variant-numeric: tabular-nums; }

.is-good, .cf-seg-grid dd.is-good, .cf-table td.is-good { color: var(--color-green-dark); }
.is-bad, .cf-seg-grid dd.is-bad, .cf-table td.is-bad { color: var(--color-red-dark); }

.cf-segments { display: grid; grid-template-columns: 1fr 1.4fr 1fr; gap: 10px; margin-top: 22px; }
.cf-seg { padding: 14px 16px; border-radius: 10px; border: 1px solid var(--color-gray-200); }
.cf-seg.is-leavers { background: var(--color-red-tint); border-color: var(--color-red-light); }
.cf-seg.is-joiners { background: var(--color-neutral-50); border-color: var(--color-neutral-200); }
.cf-seg-head { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
.cf-seg-name { font-size: 13px; font-weight: 600; color: var(--color-gray-900); }
.cf-seg.is-leavers .cf-seg-name { color: var(--color-red-dark); }
.cf-seg.is-joiners .cf-seg-name { color: var(--color-blue-dark); }
.cf-seg-n { font-size: 18px; font-weight: 600; color: var(--color-gray-900); font-variant-numeric: tabular-nums; }
.cf-seg-n small { font-size: 12px; font-weight: 400; color: var(--color-gray-500); }
.cf-seg-s { margin-top: 6px; font-size: 12px; color: var(--color-gray-600); }
.cf-seg-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin: 10px 0 0; }
.cf-seg-grid dt { font-size: 11px; color: var(--color-gray-500); }
.cf-seg-grid dd { margin: 2px 0 0; font-size: 14px; font-weight: 600; color: var(--color-gray-900); font-variant-numeric: tabular-nums; }
.cf-seg-grid dd small { display: block; font-size: 11px; font-weight: 400; color: var(--color-gray-500); }

.cf-scroll { max-height: 440px; overflow: auto; border: 1px solid var(--color-gray-200); border-radius: 8px; }
.cf-scroll-sm { max-height: 320px; }
.cf-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 12.5px; font-variant-numeric: tabular-nums; }
.cf-table th { position: sticky; top: 0; z-index: 1; background: var(--color-gray-50); color: var(--color-gray-600); font-weight: 500; text-align: right; padding: 8px 12px; border-bottom: 1px solid var(--color-gray-200); white-space: nowrap; cursor: pointer; }
.cf-table thead tr:nth-child(2) th { top: 33px; font-size: 11.5px; color: var(--color-gray-500); }
.cf-table th.is-group { text-align: center; border-left: 1px solid var(--color-gray-200); }
.cf-table th.is-group.is-leavers { color: var(--color-red-dark); }
.cf-table th.is-group.is-joiners { color: var(--color-blue-dark); }
.cf-table th.is-left, .cf-table td.is-left { text-align: left; }
.cf-table td { padding: 8px 12px; text-align: right; color: var(--color-gray-800); border-bottom: 1px solid var(--color-gray-100); white-space: nowrap; }
.cf-table td.is-leavers { color: var(--color-red-dark); }
.cf-table td.is-joiners { color: var(--color-blue-dark); }
.cf-table td b { font-weight: 600; }
.cf-table tbody tr { cursor: pointer; }
.cf-table tbody tr:hover td { background: var(--color-gray-50); }
.cf-table tbody tr.is-selected td { background: var(--color-accent-50); }
.cf-table tbody tr.is-leavers td { color: var(--color-red-dark); }
.cf-table tbody tr.is-joiners td { color: var(--color-blue-dark); }
.cf-patients { margin-top: 16px; }

@media (max-width: 1100px) {
  .cf-bridge { grid-template-columns: 1fr 1fr; }
  .cf-op { display: none; }
  .cf-segments { grid-template-columns: 1fr; }
}
</style>
